import React, { useState } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

// Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";

// Services
import AppBannerapi from "Services/AppBanner";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AppBannerCreate = (props) => {
  const { openDialog, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );
  const [bannerImagePreview, setBannerImagePreview] = useState(null);

  const maxFileSize = 5242880;

  const {
    getRootProps: rootForBanner,
    getInputProps: inputForBanner,
    open: openForBanner,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          formik.setFieldValue("bannerImage", file);
          setBannerImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleAddBanner = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = new FormData();
    data.append("bannerName", formValues?.bannerName?.trim() || "");
    data.append("bannerImage", formValues?.bannerImage);
    if (formValues?.expireDate) {
      data.append("expireDate", formValues?.expireDate);
    }
    if (formValues?.description) {
      data.append("description", formValues?.description);
    }
    data.append("status", formValues?.status);

    if (demoaccount_id) {
      data.append("demoaccount_id", demoaccount_id);
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "App Banner Created Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );

        formik.resetForm();
        setBannerImagePreview(null);
        handleClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while creating App Banner",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    AppBannerapi.addAppBanner(data).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      bannerName: "",
      bannerImage: null,
      expireDate: "",
      description: "",
      status: 1,
    },
    validationSchema: yup.object({
      bannerName: yup.string().required("Banner Name is required"),
      bannerImage: yup.mixed().required("Banner Image is required"),
      expireDate: yup.string().nullable(),
      description: yup.string().nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleAddBanner,
  });

  const handleModalClose = () => {
    formik.resetForm();
    setBannerImagePreview(null);
    handleClose();
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleModalClose}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.headerBar}>
        <Typography className={classes.headerTitle}>Add App Banner</Typography>
        <IconButton aria-label="close" onClick={handleModalClose} className={classes.closeIconBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.contentContainer}>
        <Typography className={classes.sectionTitle}>Banner Image</Typography>

        <Box {...rootForBanner()} onClick={openForBanner} className={classes.uploadBox}>
          <input {...inputForBanner()} />
          {bannerImagePreview ? (
            <Box
              component="img"
              src={bannerImagePreview}
              alt="Banner preview"
              className={classes.previewImage}
            />
          ) : (
            <>
              <FileUploadOutlinedIcon className={classes.uploadIcon} />
              <Typography className={classes.uploadText}>
                Click to upload banner image
              </Typography>
            </>
          )}
        </Box>
        {formik.errors.bannerImage && formik.touched.bannerImage && (
          <Typography color="error" variant="caption" display="block" sx={{ mt: -1, mb: 2, textAlign: "center" }}>
            {formik.errors.bannerImage}
          </Typography>
        )}

        <Typography className={classes.fieldLabel}>Banner Name</Typography>
        <TextField
          name="bannerName"
          id="bannerName"
          placeholder="Enter banner name"
          value={formik?.values?.bannerName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik?.errors?.bannerName && formik?.touched?.bannerName)}
          helperText={
            formik?.errors?.bannerName && formik?.touched?.bannerName
              ? formik?.errors?.bannerName
              : ""
          }
          fullWidth
          variant="outlined"
          className={classes.textField}
        />

        <Typography className={classes.fieldLabel}>Expire Date</Typography>
        <TextField
          name="expireDate"
          id="expireDate"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={formik?.values?.expireDate}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          variant="outlined"
          className={classes.textField}
        />

        <Typography className={classes.fieldLabel}>Description</Typography>
        <TextField
          name="description"
          id="description"
          placeholder="Enter description"
          value={formik?.values?.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          className={classes.textField}
        />

        <Typography className={classes.fieldLabel}>Status</Typography>
        <FormControl fullWidth className={classes.textField}>
          <Select
            name="status"
            value={formik.values.status}
            onChange={formik.handleChange}
            size="small"
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>

        <Box className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleModalClose} className={classes.cancelBtn}>
            Close
          </Button>
          <Button variant="contained" onClick={formik?.handleSubmit} className={classes.submitBtn}>
            Create Banner
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AppBannerCreate;
